import { Injectable, OnDestroy, OnInit } from '@angular/core';
import { of, zip } from 'rxjs';
import { BehaviorSubject, Observable, ReplaySubject, Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { IParentEntity } from '../interfaces/entity';
import { File } from '../models/file';
import { SubjectUrl } from '../models/subject-url';
import { FileService } from './file.service';
import { SubjectUrlService } from './subject-url.service';
import { CasePrepExportStatus, HyperLawService } from './hyper-law.service';
import { EventService } from './event.service';
import { AutoUnsubscribe } from 'auto-unsubscribe.decorator';
import { HttpErrorResponse } from '@angular/common/http';
import { messageService } from './messages/message.module';
import { KeyValuePair } from '../models/keyvaluepair';

@AutoUnsubscribe()
@Injectable({ providedIn: 'root' })
export class AttachmentService implements OnDestroy, OnInit {
  onChanged: BehaviorSubject<any> = new BehaviorSubject([]);
  selectedFiles: BehaviorSubject<File[]> = new BehaviorSubject([]);
  onApplyFilter: ReplaySubject<any> = new ReplaySubject<any>(1, 100);
  onApplySortOrder: ReplaySubject<any> = new ReplaySubject<any>(1, 100);

  filesChanged: Subscription;
  subjectUrlsChanged: Subscription;

  parent: IParentEntity;
  currentSortOrder: any;
  items = [];

  filters = [
    { name: 'Files', icon: 'description', attachmentType: enumAttachmentType.File, selected: true },
    { name: 'URLs', icon: 'link', attachmentType: enumAttachmentType.Url, selected: true },
  ];
  sortOrders = [{ title: 'Date', icon: '', order: 0, selected: true }, { title: 'Size' }, { title: 'Name' }];

  // hyperLawService: HyperLawService;

  constructor(
    private fileService: FileService,
    private subjectUrlService: SubjectUrlService,
    private hyperLawService: HyperLawService,
    private eventService: EventService
  ) {
    // this.hyperLawService=null; 

    this.filesChanged = this.fileService.onChanged.subscribe((files) => {
      files = files || [];
      files.forEach((i) => (i.attachmentType = enumAttachmentType.File));
      this.updateAttachments(files, enumAttachmentType.File);
    });

    this.subjectUrlsChanged = this.subjectUrlService.onChanged.subscribe((urls) => {
      urls = urls || [];
      urls.forEach((i) => (i.attachmentType = enumAttachmentType.Url));
      this.updateAttachments(urls, enumAttachmentType.Url);
    });
  }

  ngOnInit() {}

  ngOnDestroy() {
    this.filesChanged?.unsubscribe();
    this.subjectUrlsChanged?.unsubscribe();
  }

  isFile(item: File | SubjectUrl) {
    return (item as any).attachmentType === enumAttachmentType.File ? true : false;
  }

  toggleSelectFile(file: File | SubjectUrl): void {
    if (this.isFile(file)) {
      // check if hyperlaw is enabled...
      if (this.hyperLawService.isConfigValid()){
        // hyperlaw is enabled, multi-select is supported...
        const selected = this.selectedFiles.getValue();
        const index = selected.findIndex((i) => i.id === file.id);

        this.selectedFiles.next(
          !selected.length || index === -1
            ? [...selected, <File>file]
            : [...selected.slice(0, index), ...selected.slice(index + 1)]
        );
      }
      else{
        // hyperlaw is not enabled, multi-select is not supported...
        this.selectedFiles.next([<File>file]);
      }
    } else {
      this.selectedFiles.next([]);
    }
  }

  toggleSelectAll(files: (File | SubjectUrl)[]): void {
    this.selectedFiles.next(
      this.selectedFiles.getValue().length ===
        files.filter((file) => file.attachmentType == enumAttachmentType.File).length
        ? []
        : <File[]>files.filter((i) => this.isFile(i))
    );
  }

  deselectAllButOne(file: File | SubjectUrl): void {
    this.selectedFiles.next(this.isFile(file) ? [<File>file] : []);
  }

  deselectAll(): void {
    this.selectedFiles.next([]);
  }

  getAttachments(parent?: IParentEntity) {
    if (parent) {
      this.parent = parent;
      this.fileService.setParent(parent);
    }

    this.fileService.getFiles();
    if (parent) {
      this.subjectUrlService.getAll(parent.uid);
    }
  }

  clearCache() {
    if (this.fileService) {
      this.fileService.clearCache();
    }

    if (this.subjectUrlService) {
      this.subjectUrlService.clearCache();
    }
  }

  getExistingAttachments(
    parent?: IParentEntity,
    includeProperties: boolean = false,
    bForceRefresh: boolean = false
  ): Observable<any> {
    if (parent) {
      this.parent = parent;
      this.fileService.setParent(parent);
    }

    const fetch$ = [];
    fetch$.push(
      this.fileService.getExistingFiles(includeProperties).pipe(
        take(1),
        map((files) => {
          files = files || [];
          files.forEach((i) => (i.attachmentType = enumAttachmentType.File));
          return files;
        })
      )
    );

    fetch$.push(
      this.subjectUrlService.getAllData(parent.uid).pipe(
        take(1),
        map((urls: any) => {
          urls = urls || [];
          urls.forEach((i) => (i.attachmentType = enumAttachmentType.Url));
          return urls;
        })
      )
    );

    return zip(fetch$[0], fetch$[1]).pipe(
      take(1),
      map((attachments: any) => {
        return this.applySortOrder(undefined, this.applyFilter(undefined, attachments[0].concat(attachments[1])));
      })
    );
  }

  getDownloadLink(item: File | SubjectUrl): Observable<string> {
    this.eventService.fileDownloaded((item as File).fileName);
    return this.fileService.getDownloadLink((item as File).uri);
  }

  getViewOnlineLink(item: File | SubjectUrl): Observable<string> {
    return this.isFile(item)
      ? this.fileService.getViewOnlineLink((item as File).uri)
      : of((item as SubjectUrl).url.link);
  }

  deleteAttachment(item: File | SubjectUrl, subjectUid: string) {
    if (this.isFile(item)) {
      const file = item as File;
      if (file.casePrepExportStatus == CasePrepExportStatus.Success) {
        this.hyperLawService.deleteDocuments(subjectUid, [file]).subscribe({
          next: (fileUidsForFailedResponses: string[]) => {
            if (fileUidsForFailedResponses.indexOf(file.uid) == -1) {
              messageService.success('Document deleted from case preparation file');
              this.deleteFileAttachment(file);
            } else {
              messageService.error('Failed to delete document from case preparation file');
            }
          },
          error: () => {
            messageService.error('Failed to delete document from case preparation file');
          },
        });
      } else {
        if (file.casePrepExportStatus == CasePrepExportStatus.Failure) {
          this.fileService.updateMetadata(file, [new KeyValuePair('CasePrepExportStatus', '')]);
        }
        this.deleteFileAttachment(file);
      }
    } else {
      this.subjectUrlService.delete(item as SubjectUrl).subscribe();

      // remove the item from display whilst we wait for the delete to complete in the background
      this.items = [...this.items.filter((i) => i.id !== item.id)];
      this.refresh();
    }
  }

  deleteFileAttachment(file: File) {
    this.fileService.deleteFile(file);
    this.items = [...this.items.filter((i) => i.id !== file.id)];
    this.refresh();
  }

  addFiles(files) {
    this.fileService.uploadFiles(files).subscribe((e) => {
      this.getAttachments(this.parent);
    });
  }

  refresh() {
    this.onChanged.next(this.applySortOrder(undefined, this.applyFilter(undefined)));
    this.selectedFiles.next([]);
  }

  updateAttachments(items, attachmentType: enumAttachmentType) {
    // remove any items in our array which match the passed type, and replace with the new items of the specified type
    this.items = [...this.items.filter((i) => i.attachmentType !== attachmentType), ...items];
    this.refresh();
  }

  updateMetadata(item: File | SubjectUrl, metadata) {
    if (this.isFile(item)) {
      this.fileService.updateMetadata(item as File, metadata);
    } else {
      this.subjectUrlService.updateMetadata(item as SubjectUrl, metadata);
    }
  }

  applyFilter(filter?, attachments?) {
    attachments = attachments || this.items;

    if (filter) {
      const index = this.filters.findIndex((i) => i.name === filter.name);
      if (index > -1) {
        const f = this.filters[index];
        f.selected = !f.selected;
      }
    }
    let items = [];
    this.filters.forEach((x) => {
      items = [...(x.selected ? attachments.filter((i) => i.attachmentType === x.attachmentType) : []), ...items];
    });

    return items;
  }

  applySortOrder(sortOrder?, attachments?) {
    const items = attachments || this.onChanged.getValue();

    if (sortOrder) {
      const index = this.sortOrders.findIndex((i) => i.title === sortOrder.title);
      if (index > -1) {
        this.sortOrders.forEach((i) => (i.selected = false));
        this.sortOrders[index].selected = true;
        this.sortOrders[index].order = (this.sortOrders[index].order || 1) * -1;
        this.currentSortOrder = this.sortOrders[index];
      }
      this.onApplySortOrder.next(sortOrder);
    }

    sortOrder = sortOrder || this.currentSortOrder || this.sortOrders[0];
    const ab: number = sortOrder.order;
    const ba: number = sortOrder.order * -1;

    switch (sortOrder.title) {
      default:
      case 'Date':
        return items.sort((a, b) => {
          const x = a.lastModifiedDate
            ? new Date(a.lastModifiedDate).getTime()
            : a.url && a.url.dateAdded
            ? new Date(a.url.dateAdded).getTime()
            : 0;
          const y = b.lastModifiedDate
            ? new Date(b.lastModifiedDate).getTime()
            : b.url && b.url.dateAdded
            ? new Date(b.url.dateAdded).getTime()
            : 0;
          return x < y ? ba : x > y ? ab : 0;
        });
      case 'Size':
        return items.sort((a, b) => {
          const x = a.fileSize || 0;
          const y = b.fileSize || 0;
          return x < y ? ba : x > y ? ab : 0;
        });
      case 'Name':
        return items.sort((a, b) => {
          const x = (a.fileName || a.url.summary || '').toLowerCase();
          const y = (b.fileName || b.url.summary || '').toLowerCase();
          return x < y ? ba : x > y ? ab : 0;
        });
    }
  }

  getFilters() {
    return this.filters;
  }

  getSortOrders() {
    return this.sortOrders;
  }

  getMailMergeDataFileDownloadLink() {
    return this.fileService.getMailMergeDataFileDownloadLink();
  }
}

export enum enumAttachmentType {
  File,
  Url,
}
